import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { fetchWithAuth } from '../../lib/api';
import {
  Activity, Sparkles, Camera, Video, Wand2, UserPlus, Building2, Package, AlertTriangle, RefreshCw,
  CreditCard, ArrowUpRight, ArrowDownRight, RotateCcw
} from 'lucide-react';

const TYPE_META = {
  scan_generated:            { icon: Camera,   color: 'text-amber-600 bg-amber-50',     verb: '4K üretti' },
  ultrasound_4k_started:     { icon: Camera,   color: 'text-amber-600 bg-amber-50',     verb: '4K üretimini başlattı' },
  ultrasound_4k_completed:   { icon: Camera,   color: 'text-amber-600 bg-amber-50',     verb: '4K üretimini tamamladı' },
  scan_failed:               { icon: AlertTriangle, color: 'text-rose-600 bg-rose-50',  verb: '4K üretimi başarısız oldu' },
  ai_face_generated:         { icon: Wand2,    color: 'text-pink-600 bg-pink-50',       verb: 'AI bebek yüzü üretti' },
  video_generation_started:  { icon: Video,    color: 'text-indigo-600 bg-indigo-50',   verb: '4D video başlattı' },
  user_created:              { icon: UserPlus, color: 'text-emerald-600 bg-emerald-50', verb: 'sisteme kayıt oldu' },
  mom_registered:            { icon: UserPlus, color: 'text-fuchsia-600 bg-fuchsia-50', verb: 'anne hesabı açtı' },
  clinic_created:            { icon: Building2,color: 'text-blue-600 bg-blue-50',       verb: 'yeni klinik oluşturdu' },
  package_assigned:          { icon: Package,  color: 'text-violet-600 bg-violet-50',   verb: 'paket atadı' },
  package_purchased:         { icon: Package,  color: 'text-violet-600 bg-violet-50',   verb: 'paket satın aldı' },
  limit_exceeded:            { icon: AlertTriangle, color: 'text-rose-600 bg-rose-50',  verb: 'limit aşımı yaşadı' },
  credit_granted:            { icon: ArrowUpRight, color: 'text-emerald-600 bg-emerald-50', verb: 'kredi verdi' },
  credit_spent:              { icon: ArrowDownRight, color: 'text-rose-600 bg-rose-50',     verb: 'kredi kullandı' },
  credit_refunded:           { icon: RotateCcw, color: 'text-sky-600 bg-sky-50',        verb: 'kredi iade etti' },
  promo_used:                { icon: CreditCard, color: 'text-violet-600 bg-violet-50', verb: 'promo kodu kullandı' },
};

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.floor((now - then) / 1000));
  if (sec < 5) return 'şimdi';
  if (sec < 60) return `${sec} sn önce`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} dk önce`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} sa önce`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} gün önce`;
  return new Date(iso).toLocaleDateString('tr-TR');
}

function rowDescription(item) {
  const meta = TYPE_META[item.activity_type] || { verb: item.activity_type, icon: Activity, color: 'text-slate-500 bg-slate-50' };
  const actor = item.actor_name || 'Bir kullanıcı';
  const payload = item.payload || {};
  // Credit events: "Aktör → hedef Y kredi verdi/kullandı/iade etti"
  if (['credit_granted', 'credit_spent', 'credit_refunded'].includes(item.activity_type)) {
    const amount = payload.amount;
    const target = payload.target_name;
    const featureLabel = payload.feature_key ? ` (${payload.feature_key})` : '';
    if (target && target !== actor) {
      return <><span className="font-medium text-slate-800">{actor}</span> · <span className="text-slate-500">{target}</span> hesabına <span className="font-semibold text-slate-800">{amount}</span> {meta.verb}{featureLabel}</>;
    }
    return <><span className="font-medium text-slate-800">{actor}</span> <span className="font-semibold">{amount}</span> {meta.verb}{featureLabel}</>;
  }
  // Scan / AI face events with patient name
  const patient = item.patient_short;
  if (patient && ['scan_generated', 'ultrasound_4k_started', 'ultrasound_4k_completed', 'scan_failed', 'ai_face_generated', 'video_generation_started'].includes(item.activity_type)) {
    return <><span className="font-medium text-slate-800">{actor}</span> · <span className="text-slate-500">{patient}</span> hastası için <span className="text-slate-700">{meta.verb}</span></>;
  }
  return <><span className="font-medium text-slate-800">{actor}</span> <span className="text-slate-700">{meta.verb}</span></>;
}

export const LiveActivityFeed = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  const newIdsRef = useRef(new Set());

  const fetchFeed = async () => {
    try {
      const data = await fetchWithAuth('/superadmin/activity-feed?limit=25');
      if (Array.isArray(data)) {
        setItems((prev) => {
          const prevIds = new Set(prev.map((x) => x.id));
          data.forEach((x) => { if (!prevIds.has(x.id)) newIdsRef.current.add(x.id); });
          // expire highlight after 6s
          setTimeout(() => {
            data.forEach((x) => newIdsRef.current.delete(x.id));
            setTick((t) => t + 1);
          }, 6000);
          return data;
        });
        setError(null);
      }
    } catch (e) {
      setError(e?.message || 'Akış alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeed();
    const id = setInterval(fetchFeed, 10000);
    const rerender = setInterval(() => setTick((t) => t + 1), 30000); // refresh relative times
    return () => { clearInterval(id); clearInterval(rerender); };
  }, []);

  return (
    <Card className="border-0 shadow-lg" data-testid="live-activity-feed">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <span className="relative inline-flex">
              <span className="absolute inline-flex h-2 w-2 rounded-full bg-emerald-400 opacity-75 animate-ping"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
            </span>
            Canlı Aktivite
          </CardTitle>
          <button
            onClick={fetchFeed}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="live-feed-refresh"
            title="Yenile"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-1">10 saniyede bir güncellenir · son 25 olay</p>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Yükleniyor...</div>
        ) : error ? (
          <div className="p-6 text-center text-rose-500 text-sm" data-testid="live-feed-error">{error}</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="live-feed-empty">
            <Sparkles className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            Henüz aktivite yok. İlk olay geldiğinde burada anında görünecek.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-[480px] overflow-y-auto" data-testid="live-feed-list">
            {items.map((it) => {
              const meta = TYPE_META[it.activity_type] || { icon: Activity, color: 'text-slate-500 bg-slate-50' };
              const Icon = meta.icon;
              const isNew = newIdsRef.current.has(it.id);
              return (
                <li
                  key={it.id}
                  className={`px-5 py-3 flex items-start gap-3 transition-all ${isNew ? 'animate-[fadeSlide_0.5s_ease-out] bg-emerald-50/40' : ''}`}
                  data-testid={`live-feed-item-${it.activity_type}`}
                >
                  <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm leading-tight">{rowDescription(it)}</p>
                    <p className="text-xs text-slate-400 mt-0.5" data-testid="live-feed-time" data-tick={tick}>{timeAgo(it.created_at)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
      <style>{`
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </Card>
  );
};

export default LiveActivityFeed;
